/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Box,
  Button,
  Group,
  Modal,
  Select,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { GraphQLQuery, generateClient } from "aws-amplify/api";
import { useUserProvider } from "@/utils/UserProvider";
import { checkIfMaxCases } from "@/utils/checkSubscriptionUsage";
import { updateUser } from "@/graphql/mutations";
import useCreateCase from "@/hooks/useCreateCase";
import {
  UpdateUserInput,
  UpdateUserMutation,
} from "@/API";
import PayWallModal from "./PayWallModal";
import { trackEvent } from "@intercom/messenger-js-sdk";

const client = generateClient();

type addNewCaseProps = {
  modalOpened: boolean;
  setModalOpened: (value: boolean) => void;
  patient: any;
  setPatient: (value: string) => void;
  location: any;
  templates: any;
};
interface patientCaseData {
  title: string;
  description: string;
  caseTemplateId: string;
}

const AddNewCaseModal = (props: addNewCaseProps) => {
  const { modalOpened, setModalOpened, patient, location, templates } = props;
  const userSelector = (context: any) => [context.user];
  const { user } = useAuthenticator(userSelector);
  const { handleCreateCase } = useCreateCase();
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showBillingBtn, setShowBillingBtn] = useState(false);
  const [patientCase, setPatientCase] = useState<patientCaseData | null>({
    title: "",
    description: "",
    caseTemplateId: "",
  });
  const { revcatUser, amplifyUser } = useUserProvider();

  const [useTemplate, setUseTemplate] = useState(true);
  const [showPayWall, setShowPayWall] = useState(false);
  const [templateData, setTemplateData] = useState<any>(templates);

  useEffect(() => {
    let data: any = templates;
    if (data?.length > 0) {
      data = data.map((item: any) => {
        return { value: item.id, label: item.name };
      });
      setPatientCase((oldState: any) => {
        return {
          ...oldState,
          caseTemplateId: data[0]?.value,
        };
      });
    }
    setTemplateData(data);
  }, [templates]);

  const onChangeInput = (e: any, type: string) => {
    let dataChange = {};
    switch (type) {
      case "title":
        dataChange = {
          title: e.target.value,
        };
        break;
      case "desc":
        dataChange = {
          description: e.target.value,
        };
        break;
      case "templateId":
        console.log(e);
        dataChange = {
          caseTemplateId: e.value,
        };
        break;
    }
    setPatientCase((oldState: any) => {
      return {
        ...oldState,
        ...dataChange,
      };
    });
  };

  const closeAndResetModal = () => {
    setModalOpened(false);
    setUseTemplate(true);
    setErrorMsg("");
    setPatientCase({
      title: "",
      description: "",
      caseTemplateId: templateData[0]?.value,
    });
  };

  const handleAddCase = async () => {
    try {
      const { title, description, caseTemplateId }: any = patientCase;
      setShowBillingBtn(false);
      if (!title) {
        setErrorMsg("Please provide the case title");
        return;
      }
      if (await checkIfMaxCases(revcatUser, amplifyUser)) {
        setErrorMsg(
          "You have reached your limit in the Free tier. Please upgrade your subscription from your Profile.",
        );
        closeAndResetModal();
        setShowPayWall(true);
        return;
      }

      if (useTemplate && caseTemplateId === "") {
        setErrorMsg("Please provide a template");
        return;
      }

      setLoading(true);
      setErrorMsg("");
      
      console.log("[CASE-CREATE] Starting case creation with Supabase logging");
      console.log("Patient ID:", patient.id);
      console.log("Location:", location.id);
      console.log("Template:", useTemplate ? caseTemplateId : "No template");

      // Use the dual-write hook that includes Supabase logging
      await handleCreateCase(
        title,
        description,
        patient.id,
        useTemplate,
        caseTemplateId,
        { ensureRelationship: true }
      );
      
      console.log("[CASE-CREATE] Case created successfully");

      // increment cases created for user by 1
      const updatedUser: UpdateUserInput = {
        id: user.username,
        _version: amplifyUser._version,
        casesCreated: amplifyUser.casesCreated + 1,
        currentCasesCreated: amplifyUser.currentCasesCreated + 1,
      };
      await client.graphql<GraphQLQuery<UpdateUserMutation>>({
        query: updateUser,
        variables: { input: updatedUser },
      });
      trackEvent('create_case');
    } catch (err) {
      console.log("[CASE-CREATE] Error creating case:", err);
    }
    setLoading(false);
    closeAndResetModal();
  };

  return (
    <>
      <Modal opened={modalOpened} onClose={closeAndResetModal} title="Add case">
        <TextInput
          label="Case title"
          placeholder="Enter case title"
          value={patientCase?.title}
          onChange={(event) => onChangeInput(event, "title")}
          required
        />
        <TextInput
          label="Case description (optional)"
          placeholder="Enter case description"
          value={patientCase?.description}
          onChange={(event) => onChangeInput(event, "desc")}
        />
        {errorMsg && (
          <Box py={4}>
            <Text fz={14} fw={400} c={"red"}>
              {errorMsg}
            </Text>
            {showBillingBtn && (
              <NavLink to={"/plans"}>
                <Text td={"underline"} fz={"sm"} c={"teal"}>
                  Choose a plan
                </Text>
              </NavLink>
            )}
          </Box>
        )}
        <Switch
          checked={useTemplate}
          fw={600}
          my={10}
          labelPosition="left"
          label="Use template"
          onChange={(e) => setUseTemplate(e.currentTarget.checked)}
        />

        {templateData.length > 0 && useTemplate && (
          <Select
            placeholder="Select template"
            label="Template"
            data={templateData}
            value={patientCase?.caseTemplateId}
            onChange={(_value, option) => onChangeInput(option, "templateId")}
          />
        )}

        <Group mt="md">
          <Group mt="md">
            <Button onClick={handleAddCase} loading={loading}>
              Save
            </Button>
          </Group>
        </Group>
      </Modal>
      <PayWallModal modalOpened={showPayWall} setModalOpened={setShowPayWall} />
    </>
  );
};
export default AddNewCaseModal;
